// PUSH POC ONLY — WILL BE REMOVED WITH FULL IMPLEMENTATION

import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import Page from 'components/layouts/page/Page';
import Alert from 'components/shared/Alert';
import Button from 'components/shared/Button';
import { usePushSubscription } from 'hooks/usePushSubscription';
import { describeDevice, isIos } from 'lib/webPush';
import { PushSubscriptionRow, PushTestResult } from 'services/api/pushSubscriptions';
import { Body, BodySmall, H1, H2, Label } from 'styles/typography';

const LOG_LIMIT = 25;

function describeResult(result: PushTestResult): string {
  return `Queued for ${describeDevice(result.userAgent)}.`;
}

// What a device row said the last time we looked, so a change after a send can be reported.
type Outcome = { lastUsedAt: string | null; failedAt: string | null };

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'unknown error';
}

function yesNo(value: boolean) {
  return value ? 'Yes' : 'No';
}

/**
 * The Web Push proof of concept (OLIO-3563): enable push on this device, send yourself a test, see
 * every device you have enabled. Flag-gated (push_notifications_poc) at the route and in the API.
 */
export default function PushPocPage() {
  const push = usePushSubscription();
  const [log, setLog] = useState<string[]>([]);
  // Device outcomes as of the last send. A send only queues; the push service's answer lands on the
  // device row a moment later (the hook refetches the list), and this is how the log reports it.
  const outcomesBefore = useRef<Map<string, Outcome> | null>(null);

  const record = (line: string) =>
    setLog((previous) => [`${new Date().toLocaleTimeString()} — ${line}`, ...previous].slice(0, LOG_LIMIT));

  const handleEnable = async () => {
    try {
      const permission = await push.enable();
      record(permission === 'granted' ? 'Push enabled on this device.' : `Notification permission: ${permission}.`);
    } catch (error) {
      record(`Could not enable push: ${errorMessage(error)}`);
    }
  };

  const handleDisable = async () => {
    try {
      await push.disable();
      record('Push disabled on this device.');
    } catch (error) {
      record(`Could not disable push: ${errorMessage(error)}`);
    }
  };

  useEffect(() => {
    const before = outcomesBefore.current;
    if (!before) return;
    push.devices.forEach((device) => {
      const previous = before.get(device.id);
      if (!previous) return;
      if (device.failedAt && device.failedAt !== previous.failedAt) {
        record(`${describeDevice(device.userAgent)} is no longer subscribed — the push service rejected it.`);
        before.delete(device.id);
      } else if (device.lastUsedAt && device.lastUsedAt !== previous.lastUsedAt) {
        record(`Delivered to ${describeDevice(device.userAgent)} — accepted by the push service.`);
        before.delete(device.id);
      }
    });
  }, [push.devices]);

  const handleTest = async () => {
    outcomesBefore.current = new Map(
      push.devices.map((device) => [device.id, { lastUsedAt: device.lastUsedAt, failedAt: device.failedAt }])
    );
    try {
      const results = await push.sendTest();
      if (results.length === 0) {
        record('No devices are enabled, so nothing was sent.');
        return;
      }
      results.forEach((result) => record(describeResult(result)));
    } catch (error) {
      record(`Test send failed: ${errorMessage(error)}`);
    }
  };

  const showIosInstallHint = push.supported && isIos() && !push.standalone;
  const canEnable = push.supported && push.vapidConfigured && push.permission !== 'denied' && !push.loading;

  return (
    <Page>
      <Page.Header>
        <HeaderInner>
          <Title>Push Notifications (POC)</Title>
          <BodySmall>
            Proof of concept: browser push from Olio to this device. Nothing here is a real notification yet.
          </BodySmall>
        </HeaderInner>
      </Page.Header>
      <Page.Content>
        <Section>
          <H2>This device</H2>
          <StatusList>
            <StatusRow label='Browser supports push' value={yesNo(push.supported)} testId='status-supported' />
            <StatusRow label='Running as installed app' value={yesNo(push.standalone)} testId='status-standalone' />
            <StatusRow label='Notification permission' value={push.permission} testId='status-permission' />
            <StatusRow
              label='Push enabled on this device'
              value={push.loading ? 'Checking…' : yesNo(push.enabledOnThisDevice)}
              testId='status-enabled'
            />
            <StatusRow
              label='Server configured for push'
              value={push.loading ? 'Checking…' : yesNo(push.vapidConfigured)}
              testId='status-configured'
            />
          </StatusList>

          {!push.supported && <Alert>This browser cannot receive web push notifications.</Alert>}
          {showIosInstallHint && (
            <Alert>
              On iPhone and iPad, push only works from the Home Screen app: tap Share, then &ldquo;Add to Home
              Screen&rdquo;, and open Olio from there.
            </Alert>
          )}
          {push.permission === 'denied' && (
            <Alert>
              Notifications are blocked for this site. Allow them in your browser&rsquo;s site settings, then reload.
            </Alert>
          )}
          {push.loadError && <Alert>Could not load your push settings from the server. Reload to try again.</Alert>}
          {push.supported && !push.loading && !push.loadError && !push.vapidConfigured && (
            <Alert>This environment has no push keys configured, so push cannot be enabled here.</Alert>
          )}

          <Actions>
            {push.enabledOnThisDevice ? (
              <Button variant='outline-primary' onClick={handleDisable} loading={push.isDisabling}>
                Disable push on this device
              </Button>
            ) : (
              <Button onClick={handleEnable} loading={push.isEnabling} disabled={!canEnable}>
                Enable push on this device
              </Button>
            )}
            <Button
              variant='outline-gray'
              onClick={handleTest}
              loading={push.isSending}
              disabled={!push.vapidConfigured || push.loading}>
              Send test push
            </Button>
          </Actions>
          <BodySmall>
            Push stays on for this device until you turn it off here, even after you log out. Notifications never
            include patient details; open Olio to see more.
          </BodySmall>
        </Section>

        <Section>
          <H2>Your devices</H2>
          {push.devices.length === 0 ? (
            <Body>No devices have push enabled.</Body>
          ) : (
            <DeviceList>
              {push.devices.map((device) => (
                <DeviceItem key={device.id} data-testid='device-row'>
                  <Label>{describeDevice(device.userAgent)}</Label>
                  <BodySmall>{deviceStatus(device, push.thisDevice?.id === device.id)}</BodySmall>
                </DeviceItem>
              ))}
            </DeviceList>
          )}
        </Section>

        <Section>
          <H2>Activity</H2>
          {log.length === 0 ? (
            <Body>Nothing yet.</Body>
          ) : (
            <Log data-testid='activity-log'>
              {log.map((line, index) => (
                <li key={`${index}-${line}`}>{line}</li>
              ))}
            </Log>
          )}
        </Section>
      </Page.Content>
    </Page>
  );
}

function deviceStatus(device: PushSubscriptionRow, isThisDevice: boolean): string {
  const parts: string[] = [];
  if (isThisDevice) parts.push('this device');
  parts.push(device.failedAt ? 'no longer subscribed' : 'enabled');
  parts.push(`added ${new Date(device.createdAt).toLocaleDateString()}`);
  if (device.lastUsedAt) parts.push(`last push ${new Date(device.lastUsedAt).toLocaleString()}`);
  return parts.join(' · ');
}

function StatusRow({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <StatusItem data-testid={testId}>
      <Label>{label}</Label>
      <Body>{value}</Body>
    </StatusItem>
  );
}

const HeaderInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 24px;
`;

const Title = styled(H1)`
  font-size: 20px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 720px;
  margin-bottom: 32px;
`;

const StatusList = styled.dl`
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 24px;
  row-gap: 8px;
  margin: 0;
`;

const StatusItem = styled.div`
  display: contents;

  > * {
    margin: 0;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const DeviceList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DeviceItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
`;

const Log = styled.ol`
  list-style: none;
  margin: 0;
  padding: 12px 16px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.black10};
  font-family: monospace;
  font-size: 12px;
  line-height: 20px;
`;
